import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import EditableWrapper from "@/components/DesignSystemBuilder/components/EditableWrapper";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import {
  ManualAssistantMessage,
  useAiContextStore,
} from "@/store/zustand/aiContextStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import {
  useFeaturePrdForCanvas,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { generateJsonStream } from "@/utils/ai/utils";
import {
  fixJson,
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
} from "@/utils/utils";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useEditPrd } from "./hooks/useEditPrd";
import { SyncedTipTapEditor } from "./SyncedTipTapEditor";
import MermaidTab from "./Mermaid";
import { useSearchParams } from "react-router";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useSession } from "@clerk/clerk-react";

export type PRDVisualContext = {
  text: string;
  url?: string;
  type: string;
  name?: string;
}[];

export type PRDDesignSuggestion = {
  id: string;
  path: string;
  type: "PAGE" | "UI_COMPONENT";
  description: string;
  layout: string;
  instruction?: string;
  interactions?: {
    [key: string]: string;
  };
  estimated_wireframe_dimensions?: {
    width: number;
    height: number;
    fin: "y";
  };
  stories?: {
    description: string;
    states: {
      name?: string;
      description?: string;
    }[];
    instruction?: string;
  };
  previouslyDefined?: boolean;
  components: PRDDesignSuggestion[];
};

export type PRDDesignSuggestions = {
  pageOrComponent: PRDDesignSuggestion;
};

const throttledUpdatePrd = _.throttle(
  async (supabaseClient: any, newPrd: string, canvasId: string) => {
    await supabaseClient
      .from("feature_prd")
      .update({ prd: newPrd })
      .eq("canvas_id", canvasId);
  },
  1000,
  { leading: true, trailing: true }
);

const PRDTab = React.memo(() => {
  const canvasId = useCanvasId();

  const [searchParams, setSearchParams] = useSearchParams();
  const generatePRDFlow: boolean = searchParams.get("generate-prd") === "t";

  const featurePrd = useFeaturePrdForCanvas();
  const featurePrdRef = useRef(featurePrd);

  useEffect(() => {
    featurePrdRef.current = featurePrd;
  }, [featurePrd]);

  const featurePrdsStatus = useSupabaseStore(
    (state) => state.featurePrdsStatus
  );
  const generateText = useProjectCreateStore((state) => state.generateText);
  const generateAttachedFiles = useProjectCreateStore(
    (state) => state.generateAttachedFiles
  );

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const { session } = useSession();

  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const [generatingPrd, setGeneratingPrd] = useState(false);
  const [abortController, setAbortController] = useState(new AbortController());

  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);

  const { supabaseClient } = useSupabase();

  useEffect(() => {
    if (stopAiTrigger && abortController) {
      abortController.abort();
      // @ts-expect-error TS2345
      setAbortController(null);
    }
  }, [stopAiTrigger]);

  /**
   * Create the prd if it doesn't exist
   */
  useEffect(() => {
    if (!supabaseClient) return;

    const createPrd = async () => {
      const newPrd: any = {
        id: uuidv4().toString(),
        canvas_id: canvasId,
      };

      onDbSnapshot("featurePrds", {
        newItems: [newPrd],
      });

      await supabaseClient
        .from("feature_prd")
        .insert(newPrd)
        .eq("canvas_id", canvasId);
    };

    if (!featurePrd && featurePrdsStatus === "succeeded") {
      createPrd();
    }
  }, [featurePrd, featurePrdsStatus, supabaseClient]);

  const generatePRD = async () => {
    setGeneratingPrd(true);

    const messageText = "Generating PRD...";
    const progressMessage: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: messageText,
      progressItems: [
        {
          title: "New PRD",
          loading: true,
        },
      ],
    };
    setTempAssistantMessage(progressMessage);

    /**
     * Update based on the current response format returned from the vellum workflow
     */
    const updatePRD = (newPrd: string) => {
      onDbSnapshot("featurePrds", {
        newItems: [
          {
            ...featurePrdRef.current,
            prd: newPrd,
          },
        ],
      });

      throttledUpdatePrd(supabaseClient, newPrd, canvasId);
    };

    try {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiGeneratePrd`;

      const body = {
        visual_context:
          generateAttachedFiles && generateAttachedFiles.length > 0
            ? generateAttachedFiles.map((file) => ({
                url: file.url,
                mimeType: file.mimeType,
                type: file.type,
                name: file.name,
              }))
            : undefined,
        generateText,
      };

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const supabaseJWT = await getUnexpiredToken(session);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: newAbortController.signal,
      });

      let res = null;

      let partialResult = "";

      for await (let chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            partialResult += parsedChunk.chunk;
          }

          updatePRD(fixJson(partialResult).fixedObj?.prd || "");
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      try {
        // @ts-expect-error TS18047
        const finalPrd = res.data.steps[0].text;

        updatePRD(JSON.parse(finalPrd).prd);
      } catch (e) {
        // Sometimes the last response is improperly formatted, but this is ok because the streaming
        // should have the full response
      }

      // @ts-expect-error TS2345
      setAbortController(null);
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          succeeded: true,
        })),
      });
      toast.success("Generated PRD");
      setGeneratingPrd(false);
      return true;
    } catch (err) {
      logAxiosError("Error generating PRD", err);
      if (err.name !== "AbortError") {
        toast.error(`Error generating PRD: ` + getAxiosErorrMessage(err));
      }
      // @ts-expect-error TS2345
      setAbortController(null);
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          failed: true,
        })),
      });
      setGeneratingPrd(false);
      return false;
    } finally {
      refreshPlanInfo(session);
    }
  };

  const setFromIdeaRef = useRef(false);

  useEffect(() => {
    if (setFromIdeaRef.current) return;

    if (!session) return;

    if (generatePRDFlow && featurePrd && generateText && !featurePrd.prd) {
      setFromIdeaRef.current = true;
      generatePRD()
        .then((success) => {
          setSearchParams(
            (prev) => {
              const newParams = {
                ...Object.fromEntries(prev.entries()),
              };
              if (success) {
                newParams["generate-mermaid"] = "t";
              }
              delete newParams["generate-prd"];
              return newParams;
            },
            {
              preventScrollReset: true,
            }
          );
        })
        .catch((err) => {
          console.error("error generating PRD", err);
        });
    }
  }, [generatePRDFlow, featurePrd, Boolean(session)]);

  const onEditPrd = useEditPrd();

  const onEditTitle = useCallback(
    async (newValue: string) => {
      onEditPrd("title", newValue, true);
    },
    [onEditPrd]
  );

  return (
    <div className="flex flex-col gap-2 p-4 h-full overflow-y-scroll hide-scrollbar">
      <div className="flex flex-col justify-between items-start pb-2">
        <EditableWrapper
          value={featurePrd?.title || "Feature Title"}
          onEdit={onEditTitle}
          linearMode={true}
        >
          <div className="text-sm font-semibold">
            {featurePrd?.title || "Feature Title"}
          </div>
        </EditableWrapper>
      </div>

      <SyncedTipTapEditor
        valueInSupabase={featurePrd?.prd || ""}
        onUpdateValueInSupabase={(newValue) => onEditPrd("prd", newValue, true)}
        placeholder="Write your PRD here..."
      />
      <MermaidTab />
    </div>
  );
});

export default PRDTab;
